<script setup lang="ts">
/**
 * Login / logout button backed by MSAL. Renders nothing when real-auth is
 * disabled (`VITE_USE_REAL_AUTH=false`) — the X-Dev-User picker stays in
 * charge in that mode.
 */
import { computed, onMounted, ref } from "vue";
import {
  currentAccount,
  initMsal,
  login,
  logout,
  USE_REAL_AUTH,
} from "../auth/msal";
import { brandRef } from "../brand-state";

const loginLabel = computed(
  () => brandRef.value.loginButtonLabel || "Sign in with Microsoft",
);

const account = ref(currentAccount());
const busy = ref(false);

onMounted(async () => {
  if (!USE_REAL_AUTH) return;
  try {
    await initMsal();
    account.value = currentAccount();
  } catch (err) {
    console.warn("MSAL init failed", err);
  }
});

async function onLogin() {
  busy.value = true;
  try {
    await login();
  } finally {
    busy.value = false;
  }
}

async function onLogout() {
  busy.value = true;
  try {
    await logout();
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <div v-if="USE_REAL_AUTH" class="login">
    <template v-if="account">
      <span class="login__who">{{ account.name ?? account.username }}</span>
      <button :disabled="busy" class="login__btn" @click="onLogout">
        Sign out
      </button>
    </template>
    <template v-else>
      <button
        :disabled="busy"
        class="login__btn login__btn--primary"
        @click="onLogin"
      >
        {{ loginLabel }}
      </button>
    </template>
  </div>
</template>

<style scoped>
.login {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  font-size: 0.85rem;
  color: var(--color-muted);
}
.login__who {
  font-variant-numeric: tabular-nums;
}
.login__btn {
  padding: 0.4rem 0.85rem;
  font-size: 0.85rem;
  font-weight: 500;
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-pill);
  background: var(--glass-bg);
  color: var(--color-ink);
  cursor: pointer;
  transition:
    background var(--dur-1) var(--ease-out),
    box-shadow var(--dur-1) var(--ease-out),
    transform var(--dur-1) var(--ease-out);
}
.login__btn:hover:not(:disabled) {
  background: var(--color-surface);
  box-shadow: var(--shadow-sm);
}
.login__btn:disabled {
  opacity: 0.6;
  cursor: progress;
}
.login__btn--primary {
  background: var(--color-accent);
  color: var(--color-inverse, #fff);
  border-color: transparent;
  font-weight: 600;
}
.login__btn--primary:hover:not(:disabled) {
  background: var(--color-accent-hover);
  box-shadow: var(--shadow-glow);
  transform: translateY(-1px);
}
</style>
